// BatteryStationSheet — Wave 14cm / 14cp
// =======================================
//
// Bottom-Sheet das die Details einer Akku-Wechsel-Station zeigt. Wird von
// MapPage gerendert wenn der User auf eine Station-Pille tappt.
//
// Wave 14cp: vollständiger Reservierungs-Flow.
//   - VL-Kunden bekommen pro freiem Slot einen "Reserve"-Button.
//   - Bei Tap wird der Slot reserviert für station.reservationLeadTime
//     Minuten. UI zeigt Live-Countdown + Cancel + Extend (+30 min,
//     einmal pro Tag pro User).
//   - Non-VL-User sehen "Reserve — VL only" → Popup mit Lodge-Link.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  cancelReservation,
  extendReservation,
  fetchMyReservation,
  isSlotReserved,
  reserveSlot,
  socColor,
  type BatteryStation,
  type BatterySlot,
  type MyReservation,
} from '../lib/batteryStations';
import { useVlMembership } from '../lib/vlMembership';

interface Props {
  station: BatteryStation;
  onClose: () => void;
  /** Wave 14cp: optional Trigger den Caller weiß "Stations sollen neu
   *  gefetcht werden" (z.B. nach Reservieren / Cancel). MapPage's
   *  BatteryStationOverlay hört das und ruft refresh(). */
  onMutate?: () => void;
}

export function BatteryStationSheet({ station, onClose, onMutate }: Props) {
  // Wave 14cr: `simulating` mit raus — wir reichen das Flag an reserveSlot
  // weiter damit der Backend-VL-Gate für Admins die simulate, durchgeht.
  const { effective: isVl, simulating } = useVlMembership();
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  // Eigene Reservierung — kann von dieser Station sein ODER einer anderen.
  // Wenn von einer anderen, blocken wir den Reserve-Button mit Hinweis.
  const [myReservation, setMyReservation] = useState<MyReservation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isVl) return;
    let cancelled = false;
    void fetchMyReservation().then((r) => {
      if (!cancelled) setMyReservation(r);
    });
    return () => {
      cancelled = true;
    };
  }, [isVl, station.id]);

  const reservedHereSlotId =
    myReservation && myReservation.stationId === station.id
      ? myReservation.slotId
      : null;
  const reservedElsewhere =
    myReservation && myReservation.stationId !== station.id;

  const refresh = async () => {
    onMutate?.();
    const fresh = await fetchMyReservation();
    setMyReservation(fresh);
  };

  const handleDemoReserveClick = () => {
    setShowDemoPopup(true);
  };

  const handleReserve = async (slotId: string) => {
    setBusy(true);
    setError(null);
    const res = await reserveSlot(station.id, slotId, { simulateVl: simulating });
    setBusy(false);
    if (!res.ok) {
      setError(prettyReserveError(res.error));
      return;
    }
    await refresh();
  };

  const handleCancel = async (slotId: string) => {
    if (!confirm('Cancel your reservation?')) return;
    setBusy(true);
    setError(null);
    const res = await cancelReservation(station.id, slotId);
    setBusy(false);
    if (!res.ok) {
      setError(prettyReserveError(res.error || 'unknown'));
      return;
    }
    await refresh();
  };

  const handleExtend = async (slotId: string) => {
    setBusy(true);
    setError(null);
    const res = await extendReservation(station.id, slotId);
    setBusy(false);
    if (!res.ok) {
      setError(prettyReserveError(res.error));
      return;
    }
    await refresh();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Battery station ${station.name}`}
    >
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-md bg-turbo-900 border-t border-turbo-700/60 rounded-t-2xl shadow-2xl p-4 max-h-[85vh] overflow-y-auto"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white truncate">
              {station.name}
            </h2>
            <div className="text-[11px] text-white/55 font-mono tabular-nums mt-0.5">
              {station.lat.toFixed(5)}, {station.lon.toFixed(5)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/55 text-2xl leading-none px-2 -mr-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Meta-Info */}
        <div className="space-y-1.5 text-sm text-white/80 mb-4">
          {station.address && (
            <div>
              <span aria-hidden className="mr-1.5">
                📍
              </span>
              {station.address}
            </div>
          )}
          {station.contactNames && (
            <div>
              <span aria-hidden className="mr-1.5">
                👋
              </span>
              {station.contactNames}
            </div>
          )}
          {station.openingHours && (
            <div>
              <span aria-hidden className="mr-1.5">
                🕐
              </span>
              {station.openingHours}
            </div>
          )}
        </div>

        {/* Wave 14cp: aktive Reservierung des Users an DIESER Station */}
        {isVl && reservedHereSlotId && myReservation && (
          <ActiveReservationCard
            reservation={myReservation}
            busy={busy}
            onCancel={() => handleCancel(myReservation.slotId)}
            onExtend={() => handleExtend(myReservation.slotId)}
          />
        )}
        {isVl && reservedElsewhere && myReservation && (
          <div className="mb-3 p-3 rounded-lg bg-amber-500/15 border border-amber-500/30 text-[12px] text-amber-100 leading-snug">
            You already have an active reservation at{' '}
            <strong>{myReservation.stationName}</strong> (slot #
            {myReservation.slotNumber}). Cancel it first to reserve here.
          </div>
        )}
        {error && (
          <div className="mb-3 p-2.5 rounded-lg bg-red-500/15 border border-red-500/40 text-[12px] text-red-100">
            {error}
          </div>
        )}

        {/* Slot-Liste */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-white/55 mb-2">
            Slots ({station.slots.length})
          </div>
          {station.slots.length === 0 ? (
            <p className="text-sm text-white/55 italic">
              No batteries configured at this station yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {station.slots
                .slice()
                .sort((a, b) => a.slotNumber - b.slotNumber)
                .map((slot) => (
                  <SlotRow
                    key={slot.id}
                    slot={slot}
                    isVl={isVl}
                    busy={busy}
                    isMineHere={reservedHereSlotId === slot.id}
                    canReserve={isVl && !myReservation}
                    onReserveTap={() => {
                      if (!isVl) handleDemoReserveClick();
                      else void handleReserve(slot.id);
                    }}
                  />
                ))}
            </ul>
          )}
        </div>

        {/* Hint zur Reservation-Lead-Time */}
        {isVl && station.slots.length > 0 && !myReservation && (
          <p className="text-[11px] text-white/45 leading-snug mt-3">
            Reserving locks the slot for{' '}
            {station.reservationLeadTimeMinutes ?? 30} minutes. Show up,
            tap Go, get a fresh battery.
          </p>
        )}
      </div>

      {/* Demo-Popup für Non-VL */}
      {showDemoPopup && (
        <div
          className="absolute inset-0 z-10 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowDemoPopup(false)}
        >
          <div
            className="bg-turbo-900 border border-amber-500/40 rounded-2xl p-5 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-bold text-lg mb-2">
              Only for Vertical Lodge customers
            </h3>
            <p className="text-white/80 text-sm leading-relaxed mb-4">
              Battery swap stations are part of the{' '}
              <span className="text-sky-300 font-semibold">Vertical Lodge</span>{' '}
              all-inclusive package —{' '}
              <a
                href="https://www.nicolai-bicycles.com/s16-mgu-en"
                target="_blank"
                rel="noreferrer noopener"
                className="text-sky-300 underline underline-offset-2"
              >
                Nicolai Saturn 16 MGU
              </a>{' '}
              e-bikes with unlimited battery capacity for your stay.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDemoPopup(false)}
                className="flex-1 rounded-lg bg-turbo-700/60 text-white text-sm font-medium py-2 active:bg-turbo-700/80"
              >
                Close
              </button>
              <a
                href="https://www.vertical-lodge.com"
                target="_blank"
                rel="noreferrer noopener"
                className="flex-1 rounded-lg bg-sky-500 text-white text-sm font-semibold py-2 text-center no-underline active:bg-sky-600"
              >
                Learn more
              </a>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

/** Wave 14cp: aktive Reservierung des Users — Card oben im Sheet mit
 *  Live-Countdown + Cancel + Extend. */
function ActiveReservationCard({
  reservation,
  busy,
  onCancel,
  onExtend,
}: {
  reservation: MyReservation;
  busy: boolean;
  onCancel: () => void;
  onExtend: () => void;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const expiresAt = new Date(reservation.reservedUntil).getTime();
  const remainingMs = Math.max(0, expiresAt - now);
  const mm = Math.floor(remainingMs / 60_000);
  const ss = Math.floor((remainingMs % 60_000) / 1000);
  const expired = remainingMs <= 0;
  return (
    <div className="mb-3 p-3 rounded-lg bg-sky-500/15 border border-sky-500/40 text-white">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-sky-200">
          Your reservation
        </span>
        <span className="text-[11px] text-white/65">
          Slot #{reservation.slotNumber} · {reservation.batterySizeWh} Wh ·{' '}
          {reservation.currentSoc}%
        </span>
      </div>
      {expired ? (
        <div className="text-amber-200 text-sm font-semibold">
          Expired — reservation is no longer held.
        </div>
      ) : (
        <div className="font-mono text-2xl font-bold text-sky-100 tabular-nums">
          {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
        </div>
      )}
      <div className="flex gap-2 mt-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-100 text-sm font-semibold active:bg-red-500/30 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onExtend}
          disabled={busy || expired || reservation.extendedToday}
          className="flex-1 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-sm font-semibold active:bg-emerald-500/30 disabled:opacity-40"
          title={
            reservation.extendedToday
              ? 'Extension already used today'
              : 'Add 30 minutes (once per day)'
          }
        >
          +30 min
        </button>
      </div>
      {reservation.extendedToday && (
        <p className="text-[11px] text-white/55 mt-1.5 leading-snug">
          Extension already used today — you get one extra 30-minute
          window per calendar day.
        </p>
      )}
    </div>
  );
}

function SlotRow({
  slot,
  isVl,
  busy,
  isMineHere,
  canReserve,
  onReserveTap,
}: {
  slot: BatterySlot;
  isVl: boolean;
  busy: boolean;
  isMineHere: boolean;
  canReserve: boolean;
  onReserveTap: () => void;
}) {
  const isLarge = slot.batterySizeWh === 1060;
  const reserved = isSlotReserved(slot);
  return (
    <li className="flex items-center gap-3 p-2.5 rounded-lg bg-turbo-700/30 border border-turbo-500/20">
      <span
        className="font-mono text-xs text-white/55 w-8 shrink-0 tabular-nums"
        aria-label={`Slot number ${slot.slotNumber}`}
      >
        #{slot.slotNumber}
      </span>
      <div className="relative shrink-0">
        <span
          className="inline-block h-3.5 rounded-md"
          style={{
            width: isLarge ? '36px' : '24px',
            background: socColor(slot.currentSoc),
          }}
          aria-label={`${slot.batterySizeWh} watt-hours, ${slot.currentSoc} percent`}
        />
        {reserved && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-md"
            style={{
              background:
                'linear-gradient(to top right, transparent 45%, rgba(220, 38, 38, 0.95) 47%, rgba(220, 38, 38, 0.95) 53%, transparent 55%)',
            }}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white">
          <span className="font-semibold">{slot.currentSoc}%</span>
          <span className="text-white/55 text-xs ml-1.5">
            · {slot.batterySizeWh} Wh
          </span>
        </div>
        {reserved && !isMineHere && (
          <div className="text-[11px] text-red-300/85 mt-0.5">Reserved</div>
        )}
        {isMineHere && (
          <div className="text-[11px] text-sky-300/85 mt-0.5">
            Your reservation
          </div>
        )}
        {slot.notes && (
          <div className="text-[11px] text-white/55 mt-0.5">{slot.notes}</div>
        )}
      </div>

      {/* Wave 14cp: Per-Slot Reserve-Button */}
      {!reserved && !isMineHere && (
        <button
          type="button"
          onClick={onReserveTap}
          disabled={busy || (isVl && !canReserve)}
          className={`shrink-0 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
            isVl
              ? canReserve
                ? 'bg-sky-500 text-white active:bg-sky-600 disabled:opacity-50'
                : 'bg-turbo-700/60 text-white/40 cursor-not-allowed'
              : 'bg-amber-500/20 border border-amber-500/40 text-amber-100 active:bg-amber-500/30'
          }`}
          title={
            isVl
              ? canReserve
                ? 'Reserve this slot'
                : 'You already have an active reservation'
              : 'Vertical Lodge customers only'
          }
        >
          Reserve
        </button>
      )}
    </li>
  );
}

/** Übersetzt Backend-Error-Codes in lesbare Meldungen. */
function prettyReserveError(code: string): string {
  switch (code) {
    case 'vl_required':
      return 'Only Vertical Lodge customers can reserve.';
    case 'slot_already_reserved':
      return 'Someone else just reserved this slot — try a different one.';
    case 'already_have_active_reservation':
      return 'You already have an active reservation elsewhere. Cancel it first.';
    case 'slot_not_found':
      return 'Slot no longer exists. Refresh and try again.';
    case 'not_your_reservation':
      return "Can't act on someone else's reservation.";
    case 'reservation_expired':
      return 'Reservation has already expired.';
    case 'extension_already_used_today':
      return 'You already extended a reservation today. Try again tomorrow.';
    default:
      return `Something went wrong (${code}). Try again.`;
  }
}
